// @ts-nocheck
import customFieldTypesConfig from 'config/customFieldTypes.json';

/**
 * Get custom field type and format configuration with defaults
 * Returns { type, format, listItems } with default values if missing
 * @param {Object} fieldConfig - The field configuration from mappingConfig.columns[fieldId]
 * @returns {Object} { type, format, listItems }
 */
export const getCustomFieldType = (fieldConfig) => {
  const { defaults } = customFieldTypesConfig;

  // Check if this is a custom field (ID starts with "customField_")
  const isCustomField = fieldConfig?.mapping?.csvName
    && (fieldConfig.mapping.type || fieldConfig.mapping.format);

  if (!isCustomField && !fieldConfig?.mapping) {
    return {
      type: defaults.type,
      format: defaults.format,
      listItems: [],
    };
  }

  const mapping = fieldConfig?.mapping || {};
  const type = mapping.type || defaults.type;
  const format = mapping.format || customFieldTypesConfig.types[type]?.defaultFormat || defaults.format;
  const listItems = mapping.listItems || [];

  return {
    type,
    format,
    listItems,
  };
};

/**
 * Convert a date value from display format to ISO format for storage
 * @param {string} value - Date value in display format
 * @param {string} format - Date format pattern (e.g., "DD/MM/YYYY")
 * @returns {string} ISO date string or original value if conversion fails
 */
export const convertDateToISO = (value, format) => {
  if (!value || typeof value !== 'string') {
    return value;
  }

  try {
    // Parse according to format
    let day;
    let month;
    let year;
    let hours;
    let minutes;

    if (format.includes('DD/MM/YYYY')) {
      const parts = value.split(' ');
      const datePart = parts[0];
      const timePart = parts[1];
      const [d, m, y] = datePart.split('/');
      day = d;
      month = m;
      year = y;

      if (timePart) {
        const [h, min] = timePart.split(':');
        hours = h;
        minutes = min;
      }
    } else if (format.includes('YYYY/MM/DD')) {
      const parts = value.split(' ');
      const datePart = parts[0];
      const timePart = parts[1];
      const [y, m, d] = datePart.split('/');
      day = d;
      month = m;
      year = y;

      if (timePart) {
        const [h, min] = timePart.split(':');
        hours = h;
        minutes = min;
      }
    } else if (format.includes('YYYY-MM-DD')) {
      const parts = value.split(' ');
      const datePart = parts[0];
      const timePart = parts[1];
      const [y, m, d] = datePart.split('-');
      day = d;
      month = m;
      year = y;

      if (timePart) {
        const [h, min] = timePart.split(':');
        hours = h;
        minutes = min;
      }
    }

    if (!day || !month || !year) {
      return value; // Return original if parsing fails
    }

    // Create ISO string
    const isoDate = new Date(
      parseInt(year, 10),
      parseInt(month, 10) - 1,
      parseInt(day, 10),
      hours ? parseInt(hours, 10) : 0,
      minutes ? parseInt(minutes, 10) : 0,
    );

    if (isNaN(isoDate.getTime())) {
      return value; // Invalid date
    }

    return isoDate.toISOString();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Date conversion error:', error);
    return value; // Return original value on error
  }
};

/**
 * Convert a date value from ISO format to display format
 * @param {string} isoValue - ISO date string
 * @param {string} format - Date format pattern (e.g., "DD/MM/YYYY")
 * @returns {string} Date in display format
 */
export const convertDateFromISO = (isoValue, format) => {
  if (!isoValue) {
    return '';
  }

  try {
    const date = new Date(isoValue);
    if (isNaN(date.getTime())) {
      return isoValue; // Return original if invalid
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    let formatted = '';

    if (format.includes('DD/MM/YYYY')) {
      formatted = `${day}/${month}/${year}`;
    } else if (format.includes('YYYY/MM/DD')) {
      formatted = `${year}/${month}/${day}`;
    } else if (format.includes('YYYY-MM-DD')) {
      formatted = `${year}-${month}-${day}`;
    } else {
      formatted = `${day}/${month}/${year}`; // Default
    }

    // Add time if format includes it
    if (format.includes('HH:mm')) {
      formatted += ` ${hours}:${minutes}`;
    }

    return formatted;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Date conversion error:', error);
    return isoValue; // Return original on error
  }
};

/**
 * Convert a value according to its type and format for storage
 * @param {any} value - The value to convert
 * @param {string} type - Field type
 * @param {string} format - Field format
 * @returns {any} Converted value
 */
export const convertValueForStorage = (value, type, format) => {
  if (value === null || value === undefined || value === '') {
    return value;
  }

  switch (type) {
    case 'date':
      return convertDateToISO(value, format);
    case 'number': {
      if (format === 'integer') {
        const parsed = parseInt(value, 10);
        return isNaN(parsed) ? value : parsed;
      }
      const parsedFloat = parseFloat(value);
      return isNaN(parsedFloat) ? value : parsedFloat;
    }
    case 'boolean': {
      if (typeof value === 'boolean') {
        return value;
      }
      if (typeof value === 'string') {
        return value.toLowerCase() === 'true' || value === '1'
          || value.toLowerCase() === 'oui' || value.toLowerCase() === 'vrai';
      }
      return Boolean(value);
    }
    case 'volume':
    case 'mass': {
      // Parse as number, keep unit separate
      const numValue = parseFloat(value);
      return isNaN(numValue) ? value : numValue;
    }
    default:
      return value;
  }
};

/**
 * Convert a stored value to display format
 * @param {any} storedValue - The stored value
 * @param {string} type - Field type
 * @param {string} format - Field format
 * @returns {any} Value in display format
 */
export const convertValueFromStorage = (storedValue, type, format) => {
  if (storedValue === null || storedValue === undefined || storedValue === '') {
    return '';
  }

  switch (type) {
    case 'date':
      return convertDateFromISO(storedValue, format);
    case 'boolean':
      if (format === 'yes_no') {
        return storedValue ? 'Oui' : 'Non';
      }
      return storedValue ? 'Vrai' : 'Faux';
    default:
      return storedValue;
  }
};

/**
 * Validate a value according to its type and format
 * @param {any} value - The value to validate
 * @param {string} type - Field type
 * @param {string} format - Field format
 * @param {Array<string>} listItems - List items (for type 'list')
 * @returns {Object} { valid: boolean, error: string }
 */
export const validateCustomFieldValue = (value, type, format, listItems = []) => {
  if (value === null || value === undefined || value === '') {
    return { valid: true, error: null }; // Empty values are valid (optional fields)
  }

  switch (type) {
    case 'date': {
      // Try to parse the date
      const isoDate = convertDateToISO(value, format);
      if (isoDate === value && value !== '') {
        // Conversion failed, date is invalid
        return { valid: false, error: 'Date invalide' };
      }
      const date = new Date(isoDate);
      if (isNaN(date.getTime())) {
        return { valid: false, error: 'Date invalide' };
      }
      return { valid: true, error: null };
    }
    case 'number': {
      const num = format === 'integer' ? parseInt(value, 10) : parseFloat(value);
      if (isNaN(num)) {
        return { valid: false, error: 'Nombre invalide' };
      }
      return { valid: true, error: null };
    }
    case 'list': {
      if (!listItems.includes(value)) {
        return { valid: false, error: 'Valeur non autorisée' };
      }
      return { valid: true, error: null };
    }
    case 'boolean': {
      // Boolean values are always valid
      return { valid: true, error: null };
    }
    case 'volume':
    case 'mass': {
      const num = parseFloat(value);
      if (isNaN(num)) {
        return { valid: false, error: 'Valeur numérique requise' };
      }
      return { valid: true, error: null };
    }
    default:
      return { valid: true, error: null };
  }
};

/**
 * Get available formats for a given type
 * @param {string} type - Field type
 * @returns {Array<{value: string, label: string}>} Array of format objects { value, label }
 */
export const getFormatsForType = (type) => {
  const typeConfig = customFieldTypesConfig.types[type];
  if (!typeConfig) {
    return [];
  }

  return Object.entries(typeConfig.formats).map(([value, config]) => ({
    value,
    label: config.label || value,
  }));
};

/**
 * Get type configuration
 * @param {string} type - Field type
 * @returns {Object} Type configuration
 */
export const getTypeConfig = (type) => customFieldTypesConfig.types[type] || null;

/**
 * Get format configuration for a type
 * @param {string} type - Field type
 * @param {string} format - Format value
 * @returns {Object} Format configuration
 */
export const getFormatConfig = (type, format) => {
  const typeConfig = getTypeConfig(type);
  if (!typeConfig) {
    return null; // eslint-disable-line
  }
  return typeConfig.formats[format] || null; // eslint-disable-line
};
